<template>
<div class="chapter">
<div class="inner_page">
<div id="Chapter02">
<p class="ch-num"><a id="page_34">Chapter 2</a></p><a id="page_34">
<p class="ch-title">LOCOMOTIVES 100A1, 111, 4000, 4016, 4032, 4037, 4073–4099</p>
<p class="h1"><b>100 A1 LLOYD’S</b></p>
<p class="noindent1">Former GWR ‘Star’ class No 4009 SHOOTING STAR was re-built as a ‘Castle’ class 4-6-0 in April 1925, initially retaining its ‘Star’ class number and name. Renumbered 100 (100 A1) and renamed LLOYD’S in January 1936.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_40.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4009 SHOOTING STAR was rebuilt from a ‘Star’ class engine of the same name and number. No 4009 is thought to be on shed at Laira in this 1930s study. <i>J. J. Cunningham/Transport Treasury</i></b></p>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_41.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class No A1 seen in Swindon’s ‘A’ shop during an overhaul in January 1936. It looks likely that the picture was taken just after the ‘A1’ plate had been affixed, the LLOYD’S nameplate was absent from the supporting bracket at that time. Note the locomotive’s original 4009 number written on the dragbox. The locomotive was made serviceable and put back into traffic. However, shortly afterwards a GWR official realised that ‘A1’ simply didn’t fit the scheme of things, as it was not a number. Accordingly the decision was taken to bring ‘A1’ back to Swindon and additionally affix the number ‘100’, on the cab sides, above the ‘A1’. <i>Mike Morant Collection</i></b></p>
</div>
<p class="noindent1">Introduced into service as a ‘Castle’ class locomotive 9th April 1925. Diagram HA boiler fitted. Locomotive No 100 A1 was converted to oil-burning in 1948 with a cab roof vent fitted, and then later converted back to coal burning. This locomotive had a cut out section in the bottom cab step support.</p>
<p class="para">The locomotive’s last ‘Heavy General’ overhaul including boiler (HB 6600) and tender change took place at Swindon Works between 12th June and 4th August 1945. In January 1947 a further boiler change took place (HA 4611). The engine was the first of the class to be withdrawn in March 1950, from Swindon depot (82C). Recorded ‘Castle’ mileage 1,164,297, (total 1,974,651) service life 42 years, 10 months and 13 days. Disposal Swindon Works, May 1950.</p>
<p class="para">Tender type principal fitted dates as ‘Castle’, Churchward 3500g April 1925, Collett Intermediate 3500g January 1926, Churchward 3500g December 1926, Collett 4000g July 1929, Collett 4000g Oil 9th January 1947, Collett 4000g 13th August 1949.</p>
<div class="fig">
<p class="noindentb"><b><i>Allocated Depots from 1925</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">09/04/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">25/06/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">16/06/27</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">06/12/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">03/09/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">28/01/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">05/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">20/04/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">01/03/50</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Shooting Star</b> is another name for a meteor that burns up as it passes through the Earth’s atmosphere.</p>
<p class="noindent1"><b>A1 at Lloyd’s</b> was a category of ship in Lloyd’s Register of Shipping, which used an alphanumeric code to classify ships for insurance purposes.</p>
</a><p class="h1"><a id="page_34"></a><a id="page_35"><b>111 VISCOUNT CHURCHILL</b></a></p><a id="page_35">
<p class="noindent1">Designed by C.J. Churchward No 111 was originally built as a Pacific and named THE GREAT BEAR, it was introduced by the GWR in 1908.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_42.jpg"/></p>
<p class="fig-caption"><b>GWR No 111 THE GREAT BEAR was the first Pacific (4–6-2) locomotive built in Great Britain, and the only one built by the GWR. The locomotive is seen ‘on shed’ at Old Oak Common (PDN) circa 1915. The locomotive had by that time been fitted with a boiler top feed. The footplateman is thought to be Thomas Blackhall, an Oxfordshire man and regular driver of the 4–6-2. <i>Mike Bentley Collection</i></b></p>
</div>
<p class="noindent1">The Churchward Pacific No 111 was rebuilt as a ‘Castle’ class 4-6-0 in September 1924 (boiler HA 4608) and renamed VISCOUNT CHURCHILL. The locomotive’s last ‘Heavy Intermediate’ overhaul with boiler (HB 6634) and tender change took place at Swindon Works between 7th November and 11th December 1951. The engine was withdrawn in July 1953 from Laira MPD (83D). Recorded ‘Castle’ mileage1,462,356 (total 1,989,628), service life 45 years 5 months and 12 days. Disposal Swindon Works, October 1953.</p>
<p class="para">Tender type principal fitted dates as ‘Castle, Churchward 3500g 17th November 1928, Collett 4000g 19th August 1929.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">September 1924</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">May 1950</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_43.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 111 VISCOUNT CHURCHILL, seen at Old Oak Common depot (PDN) in 1934. The locomotive has a tall single chimney of a style which was gradually replaced from 1936 onwards. The engine is coupled with a Collett 4000 gallon/6 ton capacity tender. Note the tapered barrel buffers, original style inside valve spindle covers and curved side inside cylinder casing, plus the stencilled front buffer beam number. The original style brass beading embellishments to the cab front edge, number and nameplates and wheel splashers can also be seen. <i>Mike Morant Collection</i></b></p>
</div>
<p class="noindent1"><b>The Great Bear</b> the name refers to Ursa Major, the star constellation.</p>
<p class="noindent1"><b>Viscount Churchill</b>, Major Victor Albert Francis Charles Spencer, 1st Viscount Churchill GCVO JP (23 October 1864–3 January 1934), known as The Lord Churchill between 1886 and 1902, he was a peer and courtier. Attended Sandhurst and later became a Lieutenant in the Coldstream Guards. He was created the first Viscount Churchill in July 1902 and joined the Great Western Board in 1905, he was elected chairman in 1908 and served longer in that post than anyone else.</p>
</a><p class="h1"><a id="page_35"></a><a id="page_36"><b>4000 NORTH STAR</b></a></p><a id="page_36">
<p class="noindent1">Formerly GWR No 40 a 4–4-2 (Atlantic) which was re-built by Churchward in 1909 as a ‘Star’ class 4-6-0 retaining its original No 40 until renumbered 4000 in 1912. That locomotive was then re-built as a ‘Castle’ class 4-6-0 in November 1929. Diagram HA boiler number 4647 fitted during an ‘Intermediate’ overhaul between 29th April and 1st August 1931. The locomotive retained its original ‘Atlantic’ frames therefore the footplating was 2½ inches higher than all other ‘Star’ and ‘Castle’ class engines.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_44.jpg"/></p>
<p class="fig-caption"><b>GWR No 40 NORTH STAR seen as outshopped from Swindon Works as a ‘Star’ class 4-6-0 in September 1909, re-built as a ‘Castle’ in November 1929. <i>Authors Collection</i></b></p>
</div>
<p class="noindent1">The locomotive’s last heavy overhaul took place at Swindon Works between 10th August and 3rd October 1955, and included the fitting of a replacement boiler (HA 4629) and tender.</p>
<p class="para">The engine was withdrawn in May 1957 from Landore depot (87E). Recorded ‘Castle’ mileage 1,191,592 (total 2,110,396), service life 51 years, 1 month and 19 days. Disposal Swindon Works, July 1957.</p>
<p class="para">Tender type principal fitted dates as ‘Castle’, Collett 4000g November 1929, Hawksworth 4000g 1st April 1954, Collett 4000g 27th May 1954, Hawksworth 4000g 3rd October 1955.</p>
<div class="fig">
<p class="noindentb"><b><i>Allocated Depots post 1929</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">21/11/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">16/12/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">01/08/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SALOP Shrewsbury</p></td>
<td align="left" valign="top"><p class="table-para1">22/06/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/05/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TYS Tyseley</p></td>
<td align="left" valign="top"><p class="table-para1">03/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SALOP Shrewsbury</p></td>
<td align="left" valign="top"><p class="table-para1">04/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">04/05/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84K Chester West</p></td>
<td align="left" valign="top"><p class="table-para1">05/01/55</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">31/01/55</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87G Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">09/56</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">10/56</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_45.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4000 NORTH STAR, with a post 1936 shorter style chimney, is seen outside the works at Swindon in May 1954, at that time light repairs were carried out and a replacement Collett tender fitted. Note the delivery pipe to the rear driving wheel sanders, which was fed by a sandbox located below the footplate. <i>Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>North Star</b> also known as the Northern Pole Star, is a bright star which always appears due north in the sky.</p>
</a><p class="h1"><a id="page_36"></a><a id="page_37"><b>4016 THE SOMERSET LIGHT INFANTRY (PRINCE ALBERT’S)</b></a></p><a id="page_37">
<p class="noindent1">Formerly GWR ‘Star’ class No 4016 KNIGHT OF THE GOLDEN FLEECE, it was rebuilt as a ‘Castle’ class 4-6-0 in October 1925, initially retaining its ‘Star’ class number and name. Diagram HA boiler fitted. The locomotive was renamed in January 1938.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_46.jpg"/></p>
<p class="fig-caption"><b>Re-built from a ‘Star’ 4-6-0 ‘Castle’ class No 4016 KNIGHT OF THE GOLDEN FLEECE, is seen at Bristol Bath Road, circa 1933. Note the distinctive brass beading on the cab front edges, cab side windows, number/name plates and wheel splashers. This locomotive also had a cut out section in the bottom cab step support. <i>Ray Hinton Collection/Rail Photoprints</i></b></p>
</div>
<p class="noindent1">The locomotive’s last ‘Heavy General’ overhaul including boiler (HA 4628) and tender change took place at Swindon Works between 19th January and the 1st March 1950. The engine was withdrawn in September 1951, from Swindon depot (82C). Recorded ‘Castle’ mileage 1,186,663, (total 1,972,559) service life 43 years, 5 months and 24 days. Disposal Swindon Works, October 1951.</p>
<p class="para">Tender type principal fitted dates as ‘Castle, Collett Intermediate 3500g October 1925, Collett 4000g 2nd April 1929.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">27/02/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">01/04/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">25/05/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">16/08/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">OXY Oxley</p></td>
<td align="left" valign="top"><p class="table-para1">03/10/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">17/04/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">02/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">14/04/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">17/06/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">18/07/40</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">07/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">27/09/51</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_47.jpg"/></p>
<p class="fig-caption"><b>THE SOMERSET LIGHT INFANTRY (PRINCE ALBERT’S) is seen at Old Oak Common depot in April 1950. The locomotive was renamed in a ceremony at Paddington station on 18 February 1938. Note the regimental crest below the nameplate on the centre wheel splasher. <i>Ray Hinton Collection/Rail Photoprints</i></b></p>
</div>
<p class="noindent1"><b>Knight Of The Golden Fleece</b>, the order of the Golden Fleece was founded in Burgundy in 1430 and claimed pride of place amongst all Christian orders.</p>
<p class="noindent1"><b>The Somerset Light Infantry (Prince Albert’s)</b>, was an infantry regiment of the British Army active from 1685 to 1959.</p>
</a><p class="h1"><a id="page_37"></a><a id="page_38"><b>4032 QUEEN ALEXANDRA</b></a></p><a id="page_38">
<p class="noindent1">Formerly a GWR ‘Star’ class built in 1910 and then re-built as a ‘Castle’ class 4-6-0 in October 1925, retaining its ‘Star’ class number and name. Diagram HA boiler fitted.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_48.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ Class No 4032 QUEEN ALEXANDRA at Oxford depot circa 1946. Note the Collett 3500 gallon/5 ton 10 cwt tender. Ex ‘Star’ class rebuilds and ‘Castles’ 4073–4092 were built with ‘joggled’ frames i.e. set inward at the front end in order to clear the leading bogie wheels. <i>Norman Preedy Collection/Rail Photoprints</i></b></p>
</div>
<p class="noindent1">The locomotive’s last ‘Heavy General’ overhaul including boiler (HB 6607) and tender change took place at Swindon Works between 28th September and 2nd November 1949. The engine was withdrawn in September 1951 from Swindon depot (82C). Recorded ‘Castle’ mileage 1,225,908, (total 1,981,335) service life 41 years 27 days. Disposal Swindon Works, 1951/2.</p>
<p class="para">Tender type principal fitted dates as ‘Castle, Collett Intermediate 3500g April 1926, Collett 4000g November 1928, Collett 3500g 10th August 1946, Collett 4000g 25th January 1947.</p>
<div class="fig">
<p class="noindentb"><b><i>Allocated Depots from 1926</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">16/04/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">27/03/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">19/03/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">01/04/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TR Truro</p></td>
<td align="left" valign="top"><p class="table-para1">07/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">10/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">05/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">06/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">06/09/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">08/06/44</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">18/07/44</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">08/10/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">11/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">12/05/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">02/11/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">27/06/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83B Taunton</p></td>
<td align="left" valign="top"><p class="table-para1">10/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C</p></td>
<td align="left" valign="top"><p class="table-para1">19/09/51</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_49.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ Class 4-6-0 NO 4032 QUEEN ALEXANDRA seen at Newton Abbot in the summer of 1928. This loco was rebuilt from Star Class 4-6-0 No 4032. Note the Collett ‘Intermediate’ 3500g tender. <i>Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>Queen Alexandra</b> was a princess of Danish extraction (Alexandra of Denmark) who married Albert Edward, Prince of Wales (1863), and thereafter became the longest serving Princess of Wales. She became Queen Consort to King Edward VII after the 1902 coronation.</p>
</a><p class="h1"><a id="page_38"></a><a id="page_39"><b>4037 THE SOUTH WALES BORDERERS</b></a></p><a id="page_39">
<p class="noindent1">Formerly GWR ‘Star’ class No 4037 QUEEN PHILIPPA, rebuilt as a ‘Castle’ class 4-6-0 in June 1926 and renamed on 14th April 1937. Diagram HA boiler fitted.</p>
<p class="para">This member of the class is credited with having the greatest mileage 2,429,722 of any GWR engine. Of that total 776, 764 miles in service were run as a ‘Star’ class engine. The combined ‘Star’/’Castle’ mileages exceed any ‘Castle’ proper mileage, with 7 of the re-builds topping the 2 million mile mark. The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6684) and tender change took place at Swindon Works between 21st May September and 12th August 1960. The engine was withdrawn in September 1962 from Exeter depot (83C). Recorded Castle mileage 1,652,958, service life 51 years, 8 months and 18 days.</p>
<p class="para">Disposal Cashmores, Newport December 1962.</p>
<p class="para">Tender type principal fitted dates as ‘Castle’, Churchward 3500g June 1926, Collett Intermediate 3500g December 1926, Churchward 3500g June 1928, Collett 4000g 20th May 1930, Churchward 3500g September 1930, Collett 4000g 19th August 1931, Hawksworth 4000g March 1954, Collett 4000g 10th September 1955, Hawksworth 4000g 12th August 1960.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_50.jpg"/></p>
<p class="fig-caption"><b>No 4037 QUEEN PHILIPPA seen as a GWR ‘Star’ class 4-6-0 circa 1930. <i>Mike Bentley Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">02/07/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">27/08/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">12/11/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TYS Tyseley</p></td>
<td align="left" valign="top"><p class="table-para1">27/07/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">09/08/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">27/01/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">03/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">28/11/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">19/12/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">18/05/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83G Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">16/06/56</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">28/09/56</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">23/02/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">06/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83C Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">14/07/62</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_51.jpg"/></p>
<p class="fig-caption"><b>GWR Castle Class 4-6-0 No 4037 THE SOUTH WALES BORDERERS approaches Bristol Temple Meads in August 1960. Note the newer BR style ‘Box’ pattern inside cylinder cover with central step, Hawksworth 4000 gallon/6 ton tender, BR style parallel buffers and the regimental crest on the centre wheel splasher. Circa 1947 the brass beading was removed from the front end of the cabs on all engines up to and including No 5012. <i>Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>Queen Philippa,</b> Philippa of Hainault, daughter to Count William of Hainault, married Edward, Prince of Wales, later King Edward III of England, (Queen Consort). The Queen’s College Oxford was founded in her honour.</p>
<p class="noindent1"><b>The South Wales Borderers</b>, was an infantry regiment of the British Army which was the successor of the 24th Regiment of Foot (formed 1689). The Borderers were so named in 1881 and absorbed into the Royal Regiment of Wales in 1969.</p>
</a><p class="h1"><a id="page_39"></a><a id="page_40"><b>4073 CAERPHILLY CASTLE Preserved</b></a></p><a id="page_40">
<p class="noindent1">Prior to entering revenue earning service No 4073 was displayed at the British Empire Exhibition, Wembley. The exhibition, which should have opened in 1923 was postponed until 1924 and also re-opened for an 6 additional months in 1925. The GWR publicity team had introduced the first actually ‘new’ ‘Castle’ class 4-6-0 as ‘Britain’s most powerful express passenger locomotive’. Diagram HA boiler fitted.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_52.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ Class No 4073 CAERPHILLY CASTLE is seen at Starcross station with a down local train, on 25 July 1956. This image clearly shows the original style GWR curved sided inside cylinder casing. The steam leak indicates that the engine was only one month away from a heavy overhaul. <i>David Anderson</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">11/08/23</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Display Wembley</p></td>
<td align="left" valign="top"><p class="table-para1">08/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">06/11/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">04/03/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">02/10/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">06/04/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">OXF Oxford</p></td>
<td align="left" valign="top"><p class="table-para1">02/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">03/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">21/04/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">07/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">15/07/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh</p></td>
<td align="left" valign="top"><p class="table-para1">10/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">27/01/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">27/03/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">86C Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">23/01/57</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1">The locomotive’s last ‘Heavy General’ overhaul including boiler (HB 6638) and tender change took place at Swindon Works between 25th August 1956 and 23rd January 1957. The locomotive was withdrawn by BR in May 1960 from Cardiff Canton depot (86C) and after cosmetic restoration the engine was initially put on display at the Science Museum, London. The National Collection locomotive was later moved and placed on static display at Steam: Museum of the Great Western Railway, Swindon. Recorded mileage 1,910,730, service life 36 years, 8 months and 29 days.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g August 1923, Collett 4000g October 1930.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_53.jpg"/></p>
<p class="fig-caption"><b>The first true production ‘Castle’ Class 4-6-0 No 4073 ‘Caerphilly Castle’, with original tall chimney is seen in all its splendour at Old Oak Common circa 1933. <i>Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>Caerphilly Castle,</b> in South Wales is one of the world’s greatest surviving medieval castles. Its building was instigated in 1268 by Anglo-Norman Marcher Lord, Gilbert de Clare. The remains of the magnificent historic fortress stand on a 30 acre site and are a major tourist attraction, the castle is listed as being the second largest in the Britain.</p>
</a><p class="h1"><a id="page_40"></a><a id="page_41"><b>4074 CALDICOT CASTLE (Converted to double chimney)</b></a></p><a id="page_41">
<p class="noindent1">Introduced into service in December 1923. Diagram HA boiler fitted. During March 1924 this engine was tested with the dynamometer car between Swindon and Plymouth. The locomotive was fitted with a double chimney and 4-row superheater in April 1959 (boiler HD 7648). The locomotive’s last ‘Heavy General’ overhaul including boiler (HD 7663) and tender change took place at Swindon Works between 8th February and 19th April 1961. The engine was withdrawn in May 1963 from Old Oak Common depot (81A). Recorded mileage 1,844,072, service life 39 years, 5 months and 3 days. Disposal Swindon Works, October 1963.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g December 1923, Collett Intermediate 3500g March 1925, Churchward 3500g August 1927, Collett 4000g 11th February 1929, Hawksworth 15th February 1952, Collett 4000g 20th August 1952, Hawksworth 4000g 27th November 1952, Collett 4000g 27th March 1954, Hawksworth 15th April 1959, Collett 4000g 30th October 1959.</p>
<div class="fig-sp">
<p class="fig-img"><img alt="" class="image" src="images/ufig_54.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4074 CALDICOT CASTLE is seen passing Didcot at speed on the Oxford line with a Paddington-Worcester/Hereford service in August 1962, note the double chimney. In 1929 this engine (then with single chimney) was one of two ‘Castles’ trialled on the Cornish Riviera route against the LNER ‘A1’ Pacific No 4474 VICTOR WILD (later ‘A3’ class). The ‘A1’ was proved unsuitable for the many curves on the chosen route with the better suited ‘Castle’ engine burning less fuel whilst keeping better time. <i>David Anderson</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">12/23</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">04/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">27/05/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">24/06/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">07/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">16/09/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">28/03/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">29/08/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">08/04/40</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">26/04/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">01/11/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87G Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">09/56</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">06/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">13/07/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">12/08/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">03/05/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">31/10/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh</p></td>
<td align="left" valign="top"><p class="table-para1">17/01/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81E Didcot</p></td>
<td align="left" valign="top"><p class="table-para1">19/05/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">17/12/62</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_55.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4074 CALDICOT CASTLE (BR ‘Box’ style un-stepped inside cylinder cover) is seen at Patney &amp; Chirton station on the Berks and Hants line with a local service in July 1958. It appears that 3 maroon coaches are being removed from the rear of the train. <i>Alan Sainty Collection</i></b></p>
</div>
<p class="noindent1"><b>Caldicot Castle</b> was a once extensive stone medieval castle in Monmouthshire, south east Wales. The castle ruins, which stand in a delightful country park, are open to visitors between April and October of each year.</p>
</a><p class="h1"><a id="page_41"></a><a id="page_42"><b>4075 CARDIFF CASTLE</b></a></p><a id="page_42">
<p class="noindent1">Introduced into service in January 1924. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6642) and tender change took place at Swindon Works between 24th October and 19th December 1960. The locomotive was fitted with a nearside housing for short fire irons by BR. The engine was withdrawn in November 1961 from Old Oak Common depot (81A). Recorded mileage 1,807,802, service life 37 years, 10 months and 3 days. Disposal Swindon Works, March 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g January 1924, Collett 3500g February 1930, Collett 4000g 4 November 1931, Hawksworth 4000g 4th March 1957, Collett 4000g 26 December 1959.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_56.jpg"/></p>
<p class="fig-caption"><b>Smartly turned out GWR ‘Castle’ class 4-6-0 No 4075 CARDIFF CASTLE comes off Bristol Bath Road depot (82A) prior to working the up ‘Merchant Venturer’ to London Paddington, in 1956. ‘The Merchant Venturer’ was one of two BR/WR expresses named specifically for the Festival of Britain (1951). The title was bestowed on the then 11.15am Paddington–Weston-super-Mare via Bristol and the return 4.35 working from Weston. <i>Rail Photoprints Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">04/02/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">15/04/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">18/08/27</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">18/08/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">12/01/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">20/11/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">19/09/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">06/11/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh</p></td>
<td align="left" valign="top"><p class="table-para1">10/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">26/02/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">04/11/59</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_57.jpg"/></p>
<p class="fig-caption"><b>Recently outshopped from Swindon Works following a ‘Heavy General’ overhaul GWR ‘Castle’ 4-6-0 No 4075 CARDIFF CASTLE (original style inside cylinder covers) arrives at a snowy Bath Spa station on 11th January 1959, with the 09.05 (Sun) Paddington–Weston-super–Mare service. <i>Hugh Ballantyne/Rail Photoprints</i></b></p>
</div>
<p class="noindent1"><b>Cardiff Castle</b>, situated in the heart of the Welsh capital city, was transformed in Victorian times and rejoices in over 2000 years of Welsh history. The fascinating site is open throughout the whole year.</p>
</a><p class="h1"><a id="page_42"></a><a id="page_43"><b>4076 CARMARTHEN CASTLE</b></a></p><a id="page_43">
<p class="noindent1">Introduced into service in February 1924. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’). The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6683) and tender change took place at Swindon Works between 5th June and 15th August 1961. The engine was withdrawn in February 1963 from Llanelly depot (87F). Recorded mileage 1,697,895, service life 39 years and 2 days. Disposal Hayes Bridgend, October 1963.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g February 1924, Dean 4000g October 1927, Collett 4000g 30th January 1929, Dean 4000g 8th June 1935, Collett 4000g 26th August 1935, Hawksworth 4th February 1957, Collett 23rd October 1959.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_58.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4076 CARMARTHEN CASTLE is seen approaching Teignmouth with an up ‘Torquay Pullman’ service, circa 1931. The delightfully period image begs the question was the photographer more interested in the young lady or the express train? <i>Mike Morant Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">07/02/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">30/11/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">04/01/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">20/01/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">20/05/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">11/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">30/08/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">28/08/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">10/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">4A Shrewsbury</p></td>
<td align="left" valign="top"><p class="table-para1">17/06/40</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">24/07/42</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">08/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CHR Chester West</p></td>
<td align="left" valign="top"><p class="table-para1">25/12/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TYS Tyseley</p></td>
<td align="left" valign="top"><p class="table-para1">04/02/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CHR Chester West</p></td>
<td align="left" valign="top"><p class="table-para1">03/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">27/10/54</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84K Chester West</p></td>
<td align="left" valign="top"><p class="table-para1">01/1955</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">30/11/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87F Llanelly</p></td>
<td align="left" valign="top"><p class="table-para1">15/08/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87A Neath Court Sart</p></td>
<td align="left" valign="top"><p class="table-para1">28/05/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87F Llanelly</p></td>
<td align="left" valign="top"><p class="table-para1">16/07/62</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_59.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4076 CARMARTHEN CASTLE is seen at Plymouth in original condition, circa 1926. <i>Keith Langston Collection</i></b></p>
</div>
<p class="noindent1"><b>Carmarthen Castle</b>, in 1094 there was a Norman castle built in the town. The site of the current ruins date from 1105 and that fortification was extended in the 13th century. The castle was sacked by Owain Glyndwr in 1405, when later rebuilt it became a prison during the late 18th century.</p>
</a><p class="h1"><a id="page_43"></a><a id="page_44"><b>4077 CHEPSTOW CASTLE</b></a></p><a id="page_44">
<p class="noindent1">Introduced into service in February 1924. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6678) and tender change took place at Swindon Works between 12th September and 10th November 1960. The engine was withdrawn in August 1962 from St Philips Marsh depot (82B). Recorded mileage 1,823,488, service life 38 years, 6 months and 3 days. Disposal Cashmores, Newport, December 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g February 1924, Dean 4000g July 1928, Collett Intermediate 3500g 30th April 1929, Collett 4000g 14th November 1930, Hawksworth 4000g 28 March 1950, Collett 4000g 16th May 1952, Hawksworth 28th June 1955, Collett 4000g 19th March 1957, Hawksworth 4000g 18th January 1959, Collett 4000g 10 November 1960.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">07/02/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">08/10/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">10/07/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">10/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">14/03/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">01/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">01/03/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">04/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">14/01/40</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">03/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">30/11/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">11/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">01/47</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">11/09/54</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">05/05/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">10/02/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">88A Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">10/11/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh</p></td>
<td align="left" valign="top"><p class="table-para1">30/11/60</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Chepstow Castle</b>, in Monmouthshire, Wales is the oldest surviving post-Roman stone fortification in the UK. The castle ruins were used during the ‘Doctor Who 50th Anniversary’ TV broadcast. The site is open to the public.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_60.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4077 CHEPSTOW CASTLE, with Hawksworth tender, is seen at Bristol Temple Meads on 12th August 1960. <i>Norman Preedy Collection/Rail Photoprints</i></b></p>
</div>
</a><p class="h1"><a id="page_44"></a><a id="page_45"><b>4078 PEMBROKE CASTLE</b></a></p><a id="page_45">
<p class="noindent1">Introduced into service in February 1924. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’). The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HC 7623) change took place at Swindon Works between 17th May and 29th July 1960. The engine was withdrawn in July 1962 from Llanelly depot (87F). Recorded mileage 1,823,488, service life 38 years 6 months and 3 days. Disposal Hayes Bridgend, December 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g 24th February 1924, Collett 4000g 12th November 1929, Dean 4000g 5th July 1935, Collett 4000g 1st September 1936, Hawksworth 4000g 28th March 1950, Collett 4000g 4th December 1950, Hawksworth 4000g 23rd December 1955.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">24/02/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">23/04/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">17/12/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">4A Shrewsbury</p></td>
<td align="left" valign="top"><p class="table-para1">10/07/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">01/11/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84K Chester West</p></td>
<td align="left" valign="top"><p class="table-para1">27/01/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">15/06/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">08/11/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/07/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84C Banbury</p></td>
<td align="left" valign="top"><p class="table-para1">04/11/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">04/04/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87F Llanelly</p></td>
<td align="left" valign="top"><p class="table-para1">13/06/61</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Pembroke Castle</b>, in Wales stands on a site which was fortified as early as 1093 by Arnulf de Montgomery and the medieval fortification was expanded up to the late 12th century, and beyond. In the 15th and 16th centuries the castle was a place of peace. But at the outbreak of the English Civil War, although most of South Wales sided with the King, Pembroke declared for Parliament. It was besieged by Royalist troops but was saved after Parliamentary reinforcements arrived by sea from nearby Milford Haven.</p>
<p class="para">In 1648, when the war was at its close, Pembroke’s leaders changed sides and led a Royalist uprising. Oliver Cromwell came to Pembroke and took the castle after a seven-week siege. Its three leaders were found guilty of treason and Cromwell ordered the castle to be destroyed. Townspeople were even encouraged to disassemble the fortress and re-use its stone for their own purposes.</p>
<p class="para">World War I veteran Major-General Sir Ivor Philipps acquired the castle in 1928 and started extensive restoration work. After his death a trust was set up for the castle, jointly managed by the Philipps family and Pembroke Town council.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_61.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4078 PEMBROKE CASTLE is seen at Landore depot in 1954. Note the mechanical lubricator mounted forward of the steam pipe in this instance. <i>Rail Photoprints Collection</i></b></p>
</div>
</a><p class="h1"><a id="page_45"></a><a id="page_46"><b>4079 PENDENNIS CASTLE Preserved</b></a></p><a id="page_46">
<p class="noindent1">Introduced into service in March 1924. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6672) and tender change took place at Swindon Works between 25th August and 20th October 1961. BR fitted the locomotive with a nearside housing for the storage of short fire irons. The engine was withdrawn in May 1964 from St Philips Marsh (82B), and sold for preservation. Recorded mileage 1,758,398, service life 40 years 1 month and 26 days.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g February 1924, Dean 4000g November 1926, Collett 4000g 20th March 1929, Hawksworth 4000g 30th November 1950, Collett 4000g 31st March 1951.</p>
<p class="para">In 1925 the GWR loaned the locomotive to the London &amp; North Eastern Railway for trials against Gresley’s then new Pacifics. Working 16-coach trains on the East Coast main line from Kings Cross, the Collett designed ‘Castle’ 4-6-0 earned a lasting place in railway folklore, by comprehensively out-performing the larger LNER 4–6-2 engines!</p>
<p class="para">Thereafter the GWR sent <b>PENDENNIS CASTLE</b> to stand alongside <b>FLYING SCOTSMAN</b> at the re-opened 1925 British Empire Exhibition at Wembley, with a notice cheekily proclaiming it to be ‘the most powerful passenger express locomotive in Britain’.</p>
<p class="para">The preservation of the engine was not a straightforward affair, but it does thankfully have a happy ending. <b>PENDENNIS CASTLE</b> was purchased for preservation by Mike Higson and appeared at one of the Great Western Society’s first open days in 1965. The engine was later sold again and moved on first to Didcot, and then in the early 1970s to Steamtown Carnforth. In May 1977 the engine was sold to Hamersley Iron (later a Rio Tinto company), one of the largest iron ore producers in Australia, where it was used by the company hauling excursion trains in the care of the Pilbara Railways Historical Society.</p>
<p class="para">No 4079 steamed in Australia for the last time in October 1994 and after being stored out of use for several years the iconic locomotive was offered to the Great Western Society (GWS). Repatriated to the UK the engine arrived back in 2000. Restoration at the Didcot base of the GWS was still underway as this book went to press. Restoring steam locomotives is a costly business and in this instance can only be achieved by the addition of public donations.</p>
</a><p class="para"><a id="page_46">For the full PENDENNIS CASTLE story visit </a><a href="http://www.didcotrailwaycentre.org.uk/guide/" target="_blank">www.didcotrailwaycentre.org.uk/guide/</a></p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_62.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4079 PENDENNIS CASTLE is seen just prior to being shipped to Australia where the engine resided for 23 years before bring repatriated to the UK. <i>Keith Langston Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">04/03/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Exhibition Wembley</p></td>
<td align="left" valign="top"><p class="table-para1">17/05/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">13/04/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">25/06/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/09/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">01/12/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">14/12/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NPT Ebbw Junction</p></td>
<td align="left" valign="top"><p class="table-para1">05/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">06/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85 Gloucester Horton Rd</p></td>
<td align="left" valign="top"><p class="table-para1">01/11/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">HFD Hereford</p></td>
<td align="left" valign="top"><p class="table-para1">06/44</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85 Gloucester Horton Rd</p></td>
<td align="left" valign="top"><p class="table-para1">03/11/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">29/06/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">13/06/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83B Taunton</p></td>
<td align="left" valign="top"><p class="table-para1">13/09/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh</p></td>
<td align="left" valign="top"><p class="table-para1">18/01/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Stored</p></td>
<td align="left" valign="top"><p class="table-para1">14/03/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon (Reinstated)</p></td>
<td align="left" valign="top"><p class="table-para1">11/08/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh</p></td>
<td align="left" valign="top"><p class="table-para1">05/64</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Pendennis Castle</b>, (Device Fort or Henrician Castle) near Falmouth was an important artillery installation from 1539 until modern times in 1956. It was originally built for defending the naval and mercantile anchorages adjacent to the port of Falmouth in the time of Henry VIII. Pendennis Castle was built as one of a chain of forts running along the coast of the southern half of Britain from Hull to Milford Haven, this was in response to the threat of invasion from the French and Spanish.</p>
<div class="fig">
<p class="fig-img"><a id="page_47"><img alt="" class="image" src="images/ufig_63.jpg"/></a></p><a id="page_47">
<p class="fig-caption"><b>Smartly turned out GWR ‘Castle’ class 4-6-0 No 4079 PENDENNIS CASTLE storms through Badminton station with the 11.45 Bristol – Paddington service, on 22nd November 1959. <i>Hugh Ballantyne/Rail Photoprints</i></b></p>
</a></div><a id="page_47">
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_64.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4079 PENDENNIS CASTLE looked to be well off its home base cleaning roster, when seen at Oxford in May 1961, it was then a St Philips Marsh allocated locomotive. <i>David Anderson</i></b></p>
</div>
</a><p class="h1"><a id="page_47"></a><a id="page_48"><b>4080 POWDERHAM CASTLE</b> (Converted to double chimney)</a></p><a id="page_48">
<p class="noindent1">Introduced into service in March 1924. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’). The locomotive was fitted with a double chimney and 4-row superheater in August 1958 (boiler HD 7676 fitted). The locomotive’s last ‘Heavy General’ overhaul including boiler (HD 7647) and tender change took place at Swindon Works between 21st November 1961 and 19th January 1962. The engine was withdrawn in August 1964 from Southall depot (81C). Recorded mileage 1,974,461, service life 40 years, 4 months and 18 days. Disposal Cashmores, Newport, December 1964.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g 23rd March 1924, Collett 4000g 23rd March 1929, Hawksworth 4000g 10th February 1956, Collett 4000g 15th August 1956, Hawksworth 4000g 13th August 1958, Collett 4000g 14th April 1960, Hawksworth 4000g 19th January 1962.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_65.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4080 POWDERHAM CASTLE is seen climbing past Stoneycombe whilst assaulting Dainton Bank with a down express, in May 1957 some 15 months before being fitted with a double chimney. The engine achieved the highest recorded mileage of any ‘Castle’ proper (excluding re-built ‘Star’ class engines) and was allocated/re-allocated on 28 separate occasions. <i>Rail Photoprints Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">23/03/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">02/09/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">03/08/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">27/03/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">26/04/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">29/12/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">12/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">01/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">26/05/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">09/01/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">23/12/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">09/11/42</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">10/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">06/01/44</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">08/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">09/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">07/01/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">06/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82F Weymouth GWR</p></td>
<td align="left" valign="top"><p class="table-para1">02/06/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82D Westbury</p></td>
<td align="left" valign="top"><p class="table-para1">04/11/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">19/05/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">17/07/54</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">13/05/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84G Shrewsbury</p></td>
<td align="left" valign="top"><p class="table-para1">22/04/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">86C Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">02/08/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">88L Cardiff East Dock</p></td>
<td align="left" valign="top"><p class="table-para1">07/09/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">13/04/64</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81C Southall</p></td>
<td align="left" valign="top"><p class="table-para1">22/06/64</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_66.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4080 POWDERHAM CASTLE seen at Newport with the down ‘Capitals United Express’ in 1962, note the double chimney, Hawksworth tender, mechanical lubricator forward of the steam pipe and BR Box pattern stepped inside cylinder casing. The blade of the non-standard hand operated front cab window wiper can also be seen. Cardiff became the official capital of Wales on 20th December 1955, the 8am Paddington-Cardiff/Swansea departure and 3.55 pm return service were titled in recognition of that event. <i>Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>Powderham Castle</b> is located in Exeter, Devon and stands beside the river Exe. It is one of England’s oldest family homes which Sir Philip Courtenay commenced building it in 1391, and it has remained in the same family to the current day. The grounds and part of the castle are open to visitors on a limited basis.</p>
</a><p class="h1"><a id="page_48"></a><a id="page_49"><b>4081 WARWICK CASTLE</b></a></p><a id="page_49">
<p class="noindent1">Introduced into service in March 1924. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6674) and tender change took place at Swindon Works between 22nd August and 18th October 1959. The engine was withdrawn in February 1963 from Carmarthen depot (87G). Recorded mileage 1,894,998, service life 38 years, 11 months and 11 days. Disposal Hayes, Bridgend, October 1963.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g March 1924, Collett 4000g March 1929, Churchward 3500g 19th December 1929, Collett 4000g 18th June 1931, Hawksworth 3rd September 1952, Collett 4000g 28th August 1953, Hawksworth 4000g 3rd October 1959, Collett 4000g 22nd January 1960.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_67.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ Class 4-6-0 No 4081 WARWICK CASTLE is seen heading a Newcastle–Cardiff train west between Over Junction and Oakle Street, in September 1955. Note the BR/ER coaching stock. <i>Hugh Ballantyne/Rail Photoprints</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">06/04/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">RDG Reading W.R.</p></td>
<td align="left" valign="top"><p class="table-para1">05/03/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">15/03/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">09/01/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">08/01/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">08/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">05/11/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">04/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">12/05/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">21/04/40</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">05/40</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">03/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">12/07/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">26/11/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">10/07/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">04/11/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">86C Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">20/03/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83C Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">16/05/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">02/12/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84G Shrewsbury</p></td>
<td align="left" valign="top"><p class="table-para1">18/06/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">15/07/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87G Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">16/09/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87F Llanelly</p></td>
<td align="left" valign="top"><p class="table-para1">18/10/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87G Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">14/07/62</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_68.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ Class 4-6-0 No 4081 WARWICK CASTLE is seen at the buffer stops with a train at Swansea High Street station in 1955. Note the advertising hoardings and in particular the Wymans book stall. The station is a terminus, at the end of a short branch off the South Wales Main Line and the West Wales Line. <i>S. Rickard/J&amp;J Collection</i></b></p>
</div>
<p class="noindent1"><b>Warwick Castle</b>, has history dating back over 11 centuries. It is in modern times an entertainment centre operated by a private company, and thus a ‘pay to visit’ venue.</p>
</a><p class="h1"><a id="page_49"></a><a id="page_50"><b>4082 WINDSOR CASTLE</b></a></p><a id="page_50">
</a><p class="noindent1"><a id="page_50">Introduced into service in April 1924. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’). Re-numbered as engine No 7013 and re-named BRISTOL CASTLE in February 1952 (No 7013 was introduced in July 1948 by BR). When rostered for King George VI’s funeral train from Paddington to Windsor on 15th February 1952 locomotive No 4082 was in Swindon Works undergoing repair. To overcome the problem BR swopped the plates with those of No 7013</a><a href="#/xhtml/#fn1" id="ft1">*</a>, and they were never changed back. The ‘new’ WINDSOR CASTLE then hauled the aforementioned funeral train.</p>
<p class="para">The locomotive’s last ‘Heavy General’ overhaul including boiler (HC 7640) and tender change took place at Swindon Works between 28th October and 22nd December 1961. The engine was withdrawn in September 1964 from Tyseley depot (2A). Recorded mileage 1,898,571, service life 40 years, 4 months and 19 days. Disposal Cashmores, Newport, January 1965.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g 12th April 1924, Collett 4000g May 1929, Hawksworth 9th November 1948, Collett 4000g 14th June 1949, Hawksworth 4000g 24th January 1950, Collett 4000g 21st December 1951, Hawksworth 5th January 1954, Collett 4000g 16th March 1955.</p>
<p class="para"><a href="#/xhtml/#ft1" id="fn1">*</a>See also No 7013 BRISTOL CASTLE/WINDSOR CASTLE</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_69.jpg"/></p>
<p class="fig-caption"><b>The first GWR ‘Castle’ class No 4082 WINDSOR CASTLE, seen at Reading in 1934. Note the extra plate above the cabside numberplate, commemorating the occasion of the engine being driven from Swindon Works to Swindon Station by King George VI on April 28th 1924. Note GWR original style curved sided inside cylinder casing. <i>Rail Photoprints Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">12/04/24</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">25/05/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">13/05/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">23/08/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">16/12/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">22/02/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bath Bristol Road</p></td>
<td align="left" valign="top"><p class="table-para1">25/08/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">13/03/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85 Gloucester Horton Road</p></td>
<td align="left" valign="top"><p class="table-para1">02/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">08/10/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85 Gloucester Horton Road</p></td>
<td align="left" valign="top"><p class="table-para1">08/02/47</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">STJ Severn Tunnel Junction</p></td>
<td align="left" valign="top"><p class="table-para1">13/01/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85 Gloucester Horton Road</p></td>
<td align="left" valign="top"><p class="table-para1">09/03/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">02/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">08/02/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">05/01/54</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Stored</p></td>
<td align="left" valign="top"><p class="table-para1">22/10/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">13/04/64</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">2A Tyseley</p></td>
<td align="left" valign="top"><p class="table-para1">20/06/64</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_70.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4082 WINDSOR CASTLE (BR style stepped inside cylinder cover) climbs away from the Severn Tunnel towards Pilning with a South Wales-Paddington service, in August 1961. This engine was originally No 7013 BRISTOL CASTLE. <i>A.E.Durrant/Rail Photoprints</i></b></p>
</div>
<p class="noindent1"><b>Windsor Castle</b> is notably the oldest and largest occupied castle in the world and it is an Official Residence of the British Monarch. Public access is allowed to the imposing castle, which covers an area of approximately 13 acres and spans over 1000 years of history.</p>
<p class="h1"><a id="page_51"><b>4083 ABBOTSBURY CASTLE</b></a></p><a id="page_51">
<p class="noindent1">Introduced into service in May 1925. Diagram HA boiler fitted. The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6658) change took place at Swindon Works between 30th July and 9th October 1959. The engine was withdrawn in December 1961 from Cardiff Canton depot (88A). Recorded mileage 1,677,060, service life 36 years, 7 months and 10 days. Disposal Swindon Works, January 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g May 1925, Dean 4000g 1st June 1926, Collett 4000g 9th September 1929, Hawksworth 4000g 26th October 1946, Collett 4000g 16th January 1948, Hawksworth 4000g 10th November 1949, Collett 4000g 29th August 1951, Hawksworth 4000g 6th November 1953, Collett 4000g 2nd February 1954, Hawksworth 4000g 29th March 1955, Collett 4000g 17th April 1957, Hawksworth 4000g 21st October 1958.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_71.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4083 ABBOTSBURY CASTLE is seen at Newton Abbot (83A) in May 1958. <i>Keith Langston Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">09/05/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">05/06/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">07/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">28/12/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">08/02/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Stored</p></td>
<td align="left" valign="top"><p class="table-para1">06/09/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">09/09/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Stored</p></td>
<td align="left" valign="top"><p class="table-para1">21/02/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84G Shrewsbury. Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">15/07/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">16/11/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">24/03/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83C Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">13/09/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">88A Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">13/09/61</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_72.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4083 ABBOTSBURY CASTLE seen to the west of Brent station, with a summer Saturday working. Brent Hill, Devon is the backdrop. <i>Transport Treasury</i></b></p>
</div>
<p class="noindent1"><b>Abbotsbury Castle</b> is in fact the site of an Iron Age Fort in South West Dorset, the earthworks, which cover an area of approximately 10 acres, are situated on a rise called Wears Hill above the village of Abbotsbury located to the west of Dorchester.</p>
</a><p class="h1"><a id="page_51"></a><a id="page_52"><b>4084 ABERYSTWYTH CASTLE</b></a></p><a id="page_52">
<p class="noindent1">Introduced into service in May 1925. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy General’ overhaul including boiler (HB 6642) and tender change took place at Swindon Works between 25th June and 24th September 1958. The engine was withdrawn in October 1960 from Cardiff Canton depot (88A). Recorded mileage 1,674,812, service life 35 years, 4 months and 11 days. Disposal Swindon Works, December 1960.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g May 1925, Collett Intermediate 3500g March 1927, Collett 4000g 23rd October 1930, Hawksworth ‘Self Weighing’ 3800g 11th August 1952, Collett 4000g 6th September 1952, Hawksworth 4000g 4th February 1955, Collett 4000g 24th September 1958.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_73.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4084 ABERYSTWYTH CASTLE seen at Newton Abbot in 1926. Note the Churchward 3500g tender. <i>Mike Morant Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">30/05/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">02/12/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">07/02/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">13/08/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">03/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">19/04/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">10/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">03/07/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">21/03/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">09/42</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">10/42</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">05/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">27/08/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">09/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">03/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">04/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading</p></td>
<td align="left" valign="top"><p class="table-para1">04/11/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">86C Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">22/12/59</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Aberystwyth Castle</b> is an Edwardian fortress in Mid Wales which was built circa 1277, on the site of an earlier bailey castle. The ruins are open to the public.</p>
</a><p class="h1"><a id="page_52"></a><a id="page_53"><b>4085 BERKELEY CASTLE</b></a></p><a id="page_53">
<p class="noindent1">Introduced into service in June 1925. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6643) and tender change took place at Swindon Works between 26th May and 18th August 1958. The engine was withdrawn in May 1962 from Old Oak Common depot (81A). Recorded mileage 1,651,000, service life 36 years, 10 months and 22 days. Disposal Cashmores, Newport, December 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g May 1925, Collett Intermediate 3500g July 1927, Collett 4000g 12th June 1929, Hawksworth 4000g 12th December 1946, Collett 4000g 13th October 1950, Hawksworth 4000g 25th March 1955, Collett 4000g 25th August 1955, Hawksworth 4000g 18th August 1960.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_74.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4085 BERKELEY CASTLE is seen at Oxford (81F) in May 1961. Note the BR style parallel barrel buffers, stepped inside cylinder casing, but the original design sandboxes. <i>David Anderson</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">10/06/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">01/04/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">12/02/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">06/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">07/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">17/07/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">02/05/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">RDG Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">01/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85A Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">15/04/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85D Gloucester Horton Road</p></td>
<td align="left" valign="top"><p class="table-para1">20/05/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85A Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">18/02/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">02/05/62</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Berkeley Castle</b> (historically often spelt Berkley) is located in the town of Berkeley, Gloucestershire and its origins date back to the 11th century. It is thought to be the site of the murder of King Edward II in 1327. The ghost of King Edward II is said to walk the castle remains annually on the anniversary of his murder in October 1327. So horrific was the reported manner of his killing that his screams of pain were said to have been heard throughout, and far beyond the castle. Edward was later interred at Gloucester Cathedral. The castle has been the home of the Berkeley family from late 12th century to the present day and the grounds are opened to visitors from April to October each year.</p>
</a><p class="h1"><a id="page_53"></a><a id="page_54"><b>4086 BUILTH CASTLE</b></a></p><a id="page_54">
<p class="noindent1">Introduced into service in June 1925. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6650) and tender change took place at Swindon Works between 27th July and 21st September 1960. The loco achieved fame as the first GWR engine to reach 100mph since the earlier success of CITY OF TRURO.</p>
<p class="para">The engine was withdrawn in April 1962 from Reading WR depot (81D). Recorded mileage 1,791,633, service life 36 years, 9 months and 14 days. Disposal Cashmores, Newport, November 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g June 1925, Collett 4000g 17th December 1929, Collett 3500g 25th August 1931, Collett 4000g 31st August 1932, Hawksworth 4000g 22nd January 1950, Collett 4000g 29th March 1950, Hawksworth 4000g 20th August 1951, Collett 4000g 15th May 1952, Hawksworth 4000g 17th January 1957, Collett 4000g 10th February 1958, Hawksworth 4000g 19th April 1958, Collett 4000g 21st November 1958.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_75.jpg"/></p>
<p class="fig-caption"><b>It was blisteringly hot, hence no exhaust as ‘Castle’ Class 4-6-0 4086 BUILTH CASTLE’ and 2–6-2T No 5148 climb to Dainton tunnel with the 08.35 Liverpool – Penzance service, on 8th August 1959. <i>Hugh Ballantyne/Rail Photoprints</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">20/06/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">09/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">19/12/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">07/09/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">07/09/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Stored</p></td>
<td align="left" valign="top"><p class="table-para1">01/01/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">20/10/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">26/03/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">02/06/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">GLO Gloucester Horton Road</p></td>
<td align="left" valign="top"><p class="table-para1">01/12/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">22/06/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS Stored</p></td>
<td align="left" valign="top"><p class="table-para1">22/08/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS/85A Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">20/08/51</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87G Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">10/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">09/02/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">28/09/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">86C Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">09/12/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">10/05/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/12/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">19/01/62</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_76.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class No 4086 BUILTH CASTLE is seen on the approach to Teignmouth with ‘The Cornishman’, up train circa 1957. Note the Hawksworth straight sided tender. ‘The Cornishman’ Wolverhampton Low Level-Penzance (30/6/52) Sheffield Midland-Penzance (10/9/62) Bradford Forster Square-Penzance (14/6/65), Bradford Exchange-Penzance (1/5/67). <i>Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>Builth Castle</b> was a castle built under King Edward I, today only the mound and ditches remain, the site is just outside Builth Wells, Powys, Wales. The castle was besieged in 1294 during the revolt of Madog ap Llywelyn, and attacked by Owain Glyndwr’s forces during his revolt in the early 15th century. Repairs in 1409 reportedly cost £400 however, the castle was then destroyed by fire in the 17th century.</p>
</a><p class="h1"><a id="page_54"></a><a id="page_55"><b>4087 CARDIGAN CASTLE</b> (Converted to double chimney)</a></p><a id="page_55">
<p class="noindent1">Introduced into service in June 1925. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’) and a Davies &amp; Metcalf patent valveless lubricator. Double chimney and 4-row superheater fitted February 1958 (boiler HD 7665). The locomotive’s last ‘Heavy General’ overhaul including boiler (HD 7645) and tender change took place at Swindon Works between 19th August and 10th October 1961. The engine was withdrawn in April 1962 from Laira depot (84A). Recorded mileage 1,812,341, service life 38 years, 3 months and 10 days. Disposal Coopers Metals, Sharpness, May 1964.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g June 1925, Collett 4000g 22nd June 1929, Churchward 3500g 20th December 1929, Collett 4000g 13th August 1931, Churchward 3500g 15th June 1946, Collett 4000g 2nd November 1946, Hawksworth 4000g 31st March 1950, Collett 4000g 6th November 1951, Hawksworth 4000g 30th April 1953, Collett 4000g 5th July 1956, Hawksworth 10th October 1961.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_77.jpg"/></p>
<p class="fig-caption"><b>Laira based ‘Castle’ class 4-6-0 No 4087 CARDIGAN CASTLE freshly out of Swindon Works (General overhaul outshopped 13th August) and coupled to a Collett 4000 gallon tender is seen with a ‘running in turn’ at Reading General in August 1959. Note the double chimney, Davies &amp; Metcalf valveless lubricator with additional oil reservoir attached to the smokebox, BR pattern parallel barrel buffers and stepped inside cylinder cover. <i>Alan Sainty Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">27/06/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">16/04/27</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">24/05/27</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">08/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">18/08/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">29/07/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">07/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">21/02/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">07/05/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TR Truro</p></td>
<td align="left" valign="top"><p class="table-para1">08/07/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">15/10/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">04/02/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TR Truro</p></td>
<td align="left" valign="top"><p class="table-para1">28/06/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">03/01/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">12/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">23/04/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83G Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">10/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">08/54</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Stored</p></td>
<td align="left" valign="top"><p class="table-para1">13/08/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Phillips Marsh Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">06/04/63</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B Stored</p></td>
<td align="left" valign="top"><p class="table-para1">09/09/63</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_78.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4087 CARDIGAN CASTLE is seen passing Langley with a down fitted mixed freight and milk train in April 1951. Note by that time the locomotive had modified frames and ‘Box’ pattern inside cylinder casing, but was still to receive a double chimney, also the blade of the nearside hand operated window wiper can be seen. <i>Dave Cobbe Collection-C.R.L. Coles/Rail Photoprints</i></b></p>
</div>
<p class="noindent1"><b>Cardigan Castle</b> has its beginnings in the 12th century and represents over 900 years of Ceredigion history. Decades of neglect left the Cardigan town centre site in ruins but, after years of local campaigning the castle is being brought back to life and was reportedly on course to open its doors to the public in 2014.</p>
</a><p class="h1"><a id="page_55"></a><a id="page_56"><b>4088 DARTMOUTH CASTLE</b> (Converted to double chimney)</a></p><a id="page_56">
<p class="noindent1">Introduced into service in July 1925. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’), new cylinders and a Davies &amp; Metcalf patent valveless lubricator. Double chimney and 4-row superheater fitted May 1958 (boiler HD 7674). The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler(HD 7649) tender change and double chimney fitting took place at Swindon Works between 19th May and 8th July 1961. The engine was withdrawn in May 1964 from St Philips Marsh depot (82B). Recorded mileage 1,848,430 (to 28/12/63), service life 38 years, 9 months and 5 days. Disposal Cohens, Morriston, September 1964.</p>
<p class="para">Tender type principal fitted dates, Collett Intermediate 3500g 25th July 1925, Collett 4000g 9th January 1929, Churchward 3500g 30th October 1929, Collett 3500g 21st December 1929, Collett 4000g 18th March 1931, Dean 4000g 22nd September 1942, Collett 4000g 14th April 1943, Hawksworth 4000g 27th March 1953, Collett 4000g 28th October 1953.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_79.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4088 DARTMOUTH CASTLE seen tenderless, and with others of the class and a Mogul, at Swindon Works in January 1950. Note that the engine at that time was still un-modified. <i>Rail Photoprints Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">25/07/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">14/01/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">30/12/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">01/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">25/06/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">05/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">03/08/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">03/03/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">02/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">02/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">25/05/49</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">28/10/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">09/54</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85A Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">28/01/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">07/02/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Stored</p></td>
<td align="left" valign="top"><p class="table-para1">24/09/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh. Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">02/11/63</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Dartmouth Castle</b> is one of a pair of forts, the other being Kingswear Castle, sited to guard the mouth of the Dart Estuary in Devon. Built circa 1388 the English Heritage owned castle is open to the public.</p>
</a><p class="h1"><a id="page_56"></a><a id="page_57"><b>4089 DONNINGTON CASTLE</b></a></p><a id="page_57">
<p class="noindent1">Introduced into service in July 1925. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’). The locomotive’s last ‘Heavy General’ overhaul including boiler (HC 6697) change took place at Swindon Works between 8th August and 5th October 1961. The engine was withdrawn in September 1964 from Reading WR depot (81D). Recorded mileage 1,876,807, service life 39 years, 1 month and 30 days. Disposal Hayes, Bridgend, January 1965.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g July 1925, Collett 4000g 14th August 1929, Hawksworth 4000g 18th January 1952, Collett 4000g 1st May 1953, Hawksworth 4000g 27th November 1959, Collett 4000g August 1963.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">08/07/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">15/06/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">17/09/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">20/12/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">02/11/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">04/04/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">30/04/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">07/01/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">17/02/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">09/02/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">02/42</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">08/47</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">30/20/47</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">07/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">20/07/51</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">18/01/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">02/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">85A Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">02/12/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">05/07/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Stored</p></td>
<td align="left" valign="top"><p class="table-para1">22/10/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR. Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">31/12/63</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Donnington Castle</b> is a ruined medieval castle just north of the town of Newbury in Berkshire. It was built by Richard Abberbury and the castle was besieged several times during the English Civil War. All that remains of the castle in modern times is the substantial four towered gatehouse, and the surrounding earthworks. The castle ruins are reportedly haunted by several different ghosts. In 1990 several visitors witnessed the apparition of a white dog running down the hill from the castle towards the woods where it mysteriously vanished before reaching the nearby tree line! The 14th century English Heritage owned site is open to the public.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_80.jpg"/></p>
<p class="fig-caption"><b>Newton Abbot Shed 18th May 1958, on the left GWR ‘Castle’ class 4-6-0 No 4089 DONNINGTON CASTLE, note the post modification stepped straight sided, inside cylinder cover. <i>Hugh Ballantyne/Rail Photoprints</i></b></p>
</div>
</a><p class="h1"><a id="page_57"></a><a id="page_58"><b>4090 DORCHESTER CASTLE</b> (Converted to double chimney)</a></p><a id="page_58">
<p class="noindent1">Introduced into service in August 1925. Diagram HA boiler fitted. Modifications to this locomotive included new front section frames (straight replacing ‘joggled’). The first engine of the class to be fitted with a double chimney and 4-row superheater (which included an extended smokebox) March 1957 (boiler HD 7671). The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6688) and tender change took place at Swindon Works between 15th September and 7th November 1960. The engine was withdrawn in June 1963 from Cardiff East Dock depot (88L). Recorded mileage 1,848,646, service life 37 years, 9 months and 23 days. Disposal Cashmores Newport, June 1964.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g July 1925, Collett 4000g 21st June 1929, Churchward 3500g 21st December 1929, Collett 4000g 10th April 1930, Hawksworth 4000g 4th April 1957, Collett 4000g 1st January 1959.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_81.jpg"/></p>
<p class="fig-caption"><b>Double chimney GWR ‘Castle’ class 4-6-0 No 4090 DORCHESTER CASTLE takes water at Cardiff as it heads the last up steam hauled ‘South Wales Pullman’ on 8th September 1961. The duty fitter appears to be examining the off side brake gear. Note the extended smokebox, modified steam pipes, double chimney, external sandboxes, stepped inside cylinder cover and burnished buffers. <i>Hugh Ballantyne/Rail Photoprints</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">15/08/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">20/06/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">18/01/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">04/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">30/11/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">22/03/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">04/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">05/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">30/12/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">10/01/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">02/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">11/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">03/07/53</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">12/55</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">11/08/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87G Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">22/09/59</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">16/06/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87A Neath Court Sart</p></td>
<td align="left" valign="top"><p class="table-para1">28/08/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87A Stored</p></td>
<td align="left" valign="top"><p class="table-para1">17/04/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">89A Shrewsbury. Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">14/07/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">89A Stored</p></td>
<td align="left" valign="top"><p class="table-para1">07/09/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">88L Cardiff East Dock. Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">24/09/62</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_82.jpg"/></p>
<p class="fig-caption"><b>The last up steam hauled ‘South Wales Pullman’ prepares to leave Swansea High Street for London Paddington behind GWR ‘Castle’ class 4-6-0 No 4090 DORCHESTER CASTLE. The mechanical lubricator can clearly be seen on the platform side running board. ‘South Wales Pullman’ from Paddington-Swansea High Street introduced 27th June 1955, last titled run 8th September 1961. <i>Hugh Ballantyne/Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>Dorchester Castle</b> was in the town of Dorchester, Dorset and dated from between 1154 and 1175, becoming disused circa 1290. There are no remains to be seen and in modern times a prison building stands on the site.</p>
</a><p class="h1"><a id="page_58"></a><a id="page_59"><b>4091 DUDLEY CASTLE</b></a></p><a id="page_59">
<p class="noindent1">Introduced into service in August 1925. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6625) and tender change took place at Swindon Works between 15th December 1956 and 12th March 1957 and it was the first original built ‘Castle’ to be withdrawn. The engine was withdrawn in January 1959 from Old Oak Common depot (81A). Recorded mileage 1,691,856, service life 33 years, 5 months and 18 days. Disposal Swindon Works, March 1959.</p>
<p class="para">Tender type principal fitting dates, Dean 4000g August 1925, Churchward 3500g August 1927, Collett 4000 23rd March 1929, Collett (8 wheeled) 4000g 1st September 1931, Collett 4000g 11th September 1931, Hawksworth 4000g 2nd December 1952, Collett 4000g 15th December 1952.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">01/08/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">30/06/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">16/09/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">03/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">19/06/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">03/10/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">07/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">10/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">10/09/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SDN Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">04/08/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">01/11/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">21/03/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">03/10/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/06/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">19/01/59</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Dudley Castle</b> is a ruined castle dated circa 1066 and located in the town of Dudley, West Midlands, there is a privately run zoo in the castle’s extensive grounds. Dudley Castle is regarded as one of the most haunted castles in England. The sighting of many spirits have been reported over hundreds of years. Perhaps the most famous of all being that of the ‘so called’ Grey Lady, believed to be the spirit of Dorothy Beaumont. She lived in the castle for a time, and gave birth to a daughter there. Unfortunately the daughter died during birth and after that lady died her restless spirit is said to wander aimlessly around the castle and its grounds, whilst looking for her daughter. There is a public house on the site which is called the Grey Lady Tavern where there have been reports of ‘things going bump in the night, without explanation.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_83.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4091 DUDLEY CASTLE is seen approaching Bristol Temple Meads station, circa 1938. <i>Dave Cobbe Collection – C. R. L. Coles/Rail Photoprints</i></b></p>
</div>
</a><p class="h1"><a id="page_59"></a><a id="page_60"><b>4092 DUNRAVEN CASTLE</b></a></p><a id="page_60">
<p class="noindent1">Introduced into service in August 1925. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy General’ overhaul including boiler (HB 6604) and change took place at Swindon Works between 3rd May and 30th October 1956. The engine was withdrawn in December 1961 from Oxford depot (81F). Recorded mileage 1,718,879, service life 36 years, 4 months and 13 days. Disposal Swindon Works, January 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g August 1925, Collett Intermediate 3500g 5th January 1926, 4500 type 22nd May 1929, Collett 4000g 11th April 1930, Hawksworth 4000g 5th December 1946, Collett 4000g 11th April 1947, Hawksworth 4000g 16 July 1955, Collett 4000g 3 December 1955.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">06/08/25</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">26/05/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">05/09/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">28/04/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">15/01/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">21/02/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">07/11/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">20/03/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81F Oxford</p></td>
<td align="left" valign="top"><p class="table-para1">31/10/60</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Dunraven Castle</b> was a mansion on the South Wales coast near Southerndown. It was built in 1803 and demolished in 1963. The castle’s walled garden survived and can still be visited. The castle and surrounding parkland have been the subject of myth, rumor and tragic stories, mainly about the Vaughan family who lived there for many years. A ghostly figured called the Blue Lady is also said to have haunted the castle. During both World Wars the building was reportedly used as a hospital (Glamorgan Red Cross County Hospital), and later as a Workers Tourist Authority guest house. The beach and spectacular cliffs at both Southerndown and Dunraven Bay have been used as locations whilst filming several TV programs, including Doctor Who.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_84.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ Class 4-6-0 No 4092 DUNRAVEN CASTLE is seen at an unknown location, circa 1957. <i>Rail Photoprints Collection</i></b></p>
</div>
</a><p class="h1"><a id="page_60"></a><a id="page_61"><b>4093 DUNSTER CASTLE</b> (Converted to double chimney)</a></p><a id="page_61">
<p class="noindent1">Introduced into service in May 1926. Diagram HA boiler fitted. This was the first of the class to be built with straight pattern frames. Double chimney (included an extended smokebox) and 4-row superheater, fitted December 1957 (boiler HB 6688). The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HD 7686) and tender change took place at Swindon Works between 29th December 1961 and 9th March 1962. The engine was withdrawn in September 1964 from St Philips Marsh depot (82B). Recorded mileage 1,842,985, to end of January 1963, service life 38 years, 3 months and 5 days. Disposal Cashmores Newport, January 1965.</p>
<p class="para">Tender type principal fitting dates, Churchward 3500g 26 May 1926, Collett 4000g 14th March 1929, Hawksworth 4000g 15th March 1956, Collett 4000g 29th December 1956, Hawksworth 4000g 28th March 1960. This locomotive reportedly also ran for a short period with Collett’s ‘8 wheeled’ tender but those details were not recorded on the official record card.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_85.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4093 DUNSTER CASTLE backs out of Weymouth having arrived with a service from Bristol, 22nd July 1963. Note the Hawksworth 4000 gallon, 6 ton tender attached 28/03/60 and the stepped inside cylinder cover. <i>Rail Photoprints Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">26/05/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TYS Tyseley</p></td>
<td align="left" valign="top"><p class="table-para1">15/10/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">24/10/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">12/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">02/03/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">08/07/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">03/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">13/05/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">06/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">07/11/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">10/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87A Neath Court Sart</p></td>
<td align="left" valign="top"><p class="table-para1">13/06/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87F Llanelly</p></td>
<td align="left" valign="top"><p class="table-para1">11/02/63</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82B St Philips Marsh</p></td>
<td align="left" valign="top"><p class="table-para1">09/64</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Dunster Castle</b> is a former motte and bailey castle, now a country house, in Somerset. The Tor top castle has been fortified since the late Anglo-Saxon period. It is now a National Trust property and is open to the public.</p>
</a><p class="h1"><a id="page_61"></a><a id="page_62"><b>4094 DYNEVOR CASTLE</b></a></p><a id="page_62">
<p class="noindent1">Introduced into service in May 1926. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB type) and tender change took place at Swindon Works between 11th January and 29th March 1960. The engine was withdrawn in March 1962 from Carmarthen depot (87G). Recorded mileage 1,881,886, service life 35 years, 9 months and 18 days. Disposal Swindon Works, April 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g May 1926, Collett 4000g 19th December 1929, Hawksworth 4000g 9th December 1952, Collett 4000g 28 June 1954, Hawksworth 4000g 29 March 1960.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">05/06/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">22/12/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">26/03/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">07/07/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">25/06/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">10/10/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">07/11/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Stored</p></td>
<td align="left" valign="top"><p class="table-para1">12/01/42</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">07/11/42</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82A Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">09/12/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84A Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">07/54</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">06/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87G Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">11/09/61</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Dynevor Castle</b> (Dinefwr) in Wales overlooks the River Tywi near the town of Llandeilo, Carmarthenshire. Often described as a magical land of power and influence for more than 2,000 years, Dinefwr Park and Castle is an iconic place in the history of Wales. There are the signs of two forts which are thought to be evidence of a dominant Roman presence. The castle ruins and grounds are owned by the ‘Wildlife Trust of South and West Wales’ and lie within Dinefwr Park a designated National Trust area, accordingly the site is open to the public.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_86.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4094 DYNEVOR CASTLE is seen with a train of fitted vans at Oxford station in July 1954. <i>David Anderson</i></b></p>
</div>
</a><p class="h1"><a id="page_62"></a><a id="page_63"><b>4095 HARLECH CASTLE</b></a></p><a id="page_63">
<p class="noindent1">Introduced into service in June 1926. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6663) and tender change took place at Swindon Works between 14th December 1959 and 19th February 1960. The engine was withdrawn in December 1962 from Reading WR depot (81D). Recorded mileage 1,695,899, service life 36 years, 5 months and 26 days. Disposal R. A. Kings, Norwich, April 1964.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g 16 June 1926, Collett 4000g 17th April 1929, Hawksworth 4th November 1950, Collett 4000g 4th February 1952, Hawksworth 4000g 21st August 1953, Collett 4000g 6th December 1955.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">16/06/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">16/05/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">26/05/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">09/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">18/01/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">19/05/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">22/08/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">84C Banbury</p></td>
<td align="left" valign="top"><p class="table-para1">07/06/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">17/06/52</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83G Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">12/12/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83B Taunton</p></td>
<td align="left" valign="top"><p class="table-para1">22/04/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">16/06/60</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Stored</p></td>
<td align="left" valign="top"><p class="table-para1">14/03/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">06/10/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">06/10/62</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Harlech Castle</b> in Gwynedd, Wales, is a medieval fortification, constructed on a spur of rock close to the Irish Sea. It was built by Edward I during his invasion of Wales between 1282 and 1289. Described as one of the finest examples of late 13th century/14th century military architecture in Europe. The sea originally came much closer to Harlech than it does in modern times, accordingly a water-gate and a long flight of steps leads down from the castle to the former shore, that walkway allowed the castle to be resupplied by sea during sieges. In keeping with Edward’s other castles in North Wales, the architecture of Harlech has close to links to that found in the Kingdom of Savoy during the same period, an influence probably derived from the Savoy origins of the main architect, James of Saint George. The castle is a World Heritage Site, and it is open to the public.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_87.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4095 HARLECH CASTLE is seen leaving Swindon in April 1956. <i>A.E. Bennett/Transport Treasury</i></b></p>
</div>
</a><p class="h1"><a id="page_63"></a><a id="page_64"><b>4096 HIGHCLERE CASTLE</b></a></p><a id="page_64">
<p class="noindent1">Introduced into service in June 1926. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6660) and tender change took place at Swindon Works between 21st September and 17th November 1961. The engine was withdrawn in February 1963 from Old Oak Common depot (81A). Recorded mileage 1,958,378, service life 36 years, 7 months and 17 days. Disposal Hayes, Bridgend October 1963.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g 23rd June 1926, Collett 4000g 9th March 1929, Hawksworth 4000g 26th November 1959, Collett 4000g 7th October 1961, Hawksworth 4000g 17th November 1961.</p>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">23/06/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">02/04/28</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">15/03/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">04/04/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">02/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LDR Landore</p></td>
<td align="left" valign="top"><p class="table-para1">03/06/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">18/11/35</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">03/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">25/07/41</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">09/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">10/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">Brd Weston-super-Mare</p></td>
<td align="left" valign="top"><p class="table-para1">10/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">11/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">20/03/58</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81D Reading WR</p></td>
<td align="left" valign="top"><p class="table-para1">14/07/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">12/12/62</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Highclere Castle</b> is a Victorian country house which stands in a magnificent park designed by Capability Brown. The 1,000-acre estate is in Hampshire, about 5 miles south of Newbury, Berkshire. Admittance to the castle is seasonable and by pre ordered ticket. This building became the main location during the filming of the television series Downton Abbey.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_88.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4096 HIGHCLERE CASTLE is seen at Newton Abbot on 16th August 1957. Note GWR ‘King’ class No 6022 KING EDWARD III with the up ‘Mayflower’ working, standing in the adjacent platform. <i>Willy Hermiston/Transport Treasury</i></b></p>
</div>
</a><p class="h1"><a id="page_64"></a><a id="page_65"><b>4097 KENILWORTH CASTLE</b> (Converted to double chimney)</a></p><a id="page_65">
<p class="noindent1">Introduced into service in June 1926. Diagram HA boiler fitted. Double chimney and 4-row superheater fitted during the locomotive’s last ‘Heavy General’ overhaul which included boiler (HD 7657) and tender change, and took place at Swindon Works between 16th April and 4th June 1958. The engine was withdrawn in May 1960 from Swindon depot (82C). Recorded mileage 1,713,966, service life 33 years, 10 months and 6 days. Disposal Swindon Works June 1960.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g 30th June 1926, Dean 4000g 3rd May 1928, Collett 4000g 31st July 1929, Hawksworth 4000g 5th December 1956, Collett 4000g 4th June 1958.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_89.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4097 KENILWORTH CASTLE is examined by the rostered driver before moving off shed at Taunton on 8th June 1955. Note the shorter single chimney which contrasts well with the taller chimney on the tank locomotive to the left, BR re-build style un-stepped inside cylinder cover and (81A) Old Oak Common shed plate. The ‘Castle’ is in the company of GWR Modified ‘4500’ class 2–6-2T No 5503 which was at that time a Taunton (83B) engine. <i>David Anderson</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">30/06/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">WOS Worcester</p></td>
<td align="left" valign="top"><p class="table-para1">02/08/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CARM Carmarthen</p></td>
<td align="left" valign="top"><p class="table-para1">11/09/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">23/12/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">06/01/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">SRD Stafford Road</p></td>
<td align="left" valign="top"><p class="table-para1">07/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">24/07/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">06/01/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TR Truro</p></td>
<td align="left" valign="top"><p class="table-para1">08/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">31/12/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">24/05/45</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PZ Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">17/12/46</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">10/48</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83D Laira</p></td>
<td align="left" valign="top"><p class="table-para1">03/50</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">02/10/51</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">06/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">82C Swindon</p></td>
<td align="left" valign="top"><p class="table-para1">04/04/60</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Kenilworth Castle</b> is located in the town of the same name in Warwickshire. Constructed from Norman through to Tudor times, the castle has been described by architectural historian Anthony Emery as “the finest surviving example of a semi-royal palace of the later middle ages, significant for its scale, form and quality of workmanship”. The castle ruins and grounds are maintained by English Heritage and are open to the public.</p>
</a><p class="h1"><a id="page_65"></a><a id="page_66"><b>4098 KIDWELLY CASTLE</b></a></p><a id="page_66">
<p class="noindent1">Introduced into service in July 1926. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6664) and tender change took place at Swindon Works between 11th November 1960 and 27th January 1961. The engine was withdrawn in December 1963 from Old Oak Common depot (81A). Recorded mileage 1,723,879, service life 37 years, 4 months and 29 days. Disposal Cashmores, Great Bridge June 1964.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g July 1926, Collett 4000g 26th November 1929, Hawksworth 22nd January 1953, Collett 4000g 2nd February 1955, Hawksworth 4000g 15th March 1957, Collett 4000g 28th November 1959.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_90.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ class 4-6-0 No 4098 KIDWELLY CASTLE is seen arriving at Paddington Station. Note the Hawksworth straight sided tender with which the engine was paired between 15/03/1957 and 28/11/1959. <i>Mike Morant Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">29/07/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">02/12/29</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">BRD Bristol Bath Road</p></td>
<td align="left" valign="top"><p class="table-para1">25/10/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">12/11/30</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">06/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TN Taunton</p></td>
<td align="left" valign="top"><p class="table-para1">29/04/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">10/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">27/01/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">TR Truro</p></td>
<td align="left" valign="top"><p class="table-para1">06/03/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">18/08/37</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">24/09/38</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">18/04/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">LA Laira</p></td>
<td align="left" valign="top"><p class="table-para1">05/05/39</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">25/04/47</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83A NA Stored</p></td>
<td align="left" valign="top"><p class="table-para1">14/03/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A OOC Reinstated</p></td>
<td align="left" valign="top"><p class="table-para1">14/07/62</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">81A Stored</p></td>
<td align="left" valign="top"><p class="table-para1">28/12/63</p></td>
</tr>
</tbody>
</table>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_91.jpg"/></p>
<p class="fig-caption"><b>Looks like a ‘Big job broke out’! Three enginemen on top of the tender as Newton Abbot (83A) allocated GWR ‘Castle’ class 4-6-0 No 4098 KIDWELLY CASTLE takes water at Bristol Temple Meads station in this 1961 study. Note also Swindon built ‘Warship’ class No D820, possibly steam enthusiasts would say that the enemy is looking on! <i>Rail Photoprints Collection</i></b></p>
</div>
<p class="noindent1"><b>Kidwelly Castle</b> is a Norman castle overlooking the River Gwendraeth and the town of Kidwelly, in Carmarthenshire. The present remains of the castle include work dating from circa 1200 to circa 1476. The castle fell to the Welsh several times in the twelfth century and later in its history it was unsuccessfully besieged by forces of Owain Glyndwr in 1403. The magnificent castle ruins are open to the public.</p>
</a><p class="h1"><a id="page_66"></a><a id="page_67"><b>4099 KILGERRAN CASTLE</b></a></p><a id="page_67">
<p class="noindent1">Introduced into service in August 1926. Diagram HA boiler fitted.</p>
<p class="para">The locomotive’s last ‘Heavy Intermediate’ overhaul including boiler (HB 6616) and tender change took place at Swindon Works between 13th February and 14th April 1961. The engine was withdrawn in September 1962 from Llanelly depot (87F). Recorded mileage 1,873,985, service life 36 years, 1 month and 18 days. Disposal Hayes, Bridgend September 1962.</p>
<p class="para">Tender type principal fitted dates, Churchward 3500g August 1926, Collett 3500g 5th December 1929, Hawksworth 4000g 26th June 1959.</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_92.jpg"/></p>
<p class="fig-caption"><b>GWR ‘Castle’ 4-6-0 No 4099 KILGERRAN CASTLE is seen ex works at Swindon, in October 1957 after a ‘Heavy Intermediate’ overhaul. Note also No 7923 SPEKE HALL and Collett ‘Pannier’ tank No 6730. <i>Rail Photoprints Collection</i></b></p>
</div>
<div class="fig-sp">
<p class="noindentb"><b><i>Allocated Depots</i></b></p>
<table border="1" cellpadding="3" cellspacing="0" class="color" width="100%">
<colgroup>
<col width="70%"/>
<col width="30%"/>
</colgroup>
<tbody>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">03/08/26</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">02/03/31</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">18/04/32</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">CDF Cardiff Canton</p></td>
<td align="left" valign="top"><p class="table-para1">07/33</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">PDN Old Oak Common</p></td>
<td align="left" valign="top"><p class="table-para1">16/06/34</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">EXE Exeter</p></td>
<td align="left" valign="top"><p class="table-para1">17/10/36</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">NA Newton Abbot</p></td>
<td align="left" valign="top"><p class="table-para1">18/12/43</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">83G Penzance</p></td>
<td align="left" valign="top"><p class="table-para1">26/03/55</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87E Landore</p></td>
<td align="left" valign="top"><p class="table-para1">02/11/57</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87A Neath Court Sart</p></td>
<td align="left" valign="top"><p class="table-para1">13/05/61</p></td>
</tr>
<tr>
<td align="left" valign="top"><p class="table-para">87F Llanelly</p></td>
<td align="left" valign="top"><p class="table-para1">14/07/62</p></td>
</tr>
</tbody>
</table>
</div>
<p class="noindent1"><b>Kilgerran Castle</b> (Cilgerran Castle) is a 13th-century ruined castle located in Cilgerran, Pembrokeshire, the ruins stand on a rocky promontory above the River Teifi. The castle site is National Trust property, in the guardianship of Cadw: Welsh Historic Monuments Executive Agency, and it is open to the public.</p>
<p class="h1"><b>GWR ‘Castle’ class 4–cylinder locomotives, Swindon Works ‘Lot Numbers’</b></p>
<p class="noindentx">No 111 Originally built as Pacific THE GREAT BEAR Lot 171</p>
<p class="noindentx">No 4000 Originally built as ‘Star’ class 4–4-2 and then 4-6-0, Lot 161</p>
<p class="noindentx">No 100A1 Originally built as ‘Star’ class 4-6-0 Lot 168</p>
<p class="noindentx">No 4016 Originally built as ‘Star’ class 4-6-0 Lot 173</p>
<p class="noindentx">No 4032 Originally built as ‘Star’ class 4-6-0 Lot 180</p>
<p class="noindentx">No 4037 Originally built as ‘Star’ class 4-6-0 Lot 180</p>
<p class="noindentx">Nos 4073–4082 Lot 224 built 1923/24 (Average cost per engine £6835)</p>
<p class="noindentx">Nos 4083–4092 Lot 232 built 1925 (Average cost per engine £6644)</p>
<p class="noindentx">Nos 4093–4099 Lot 234 built 1926 (Average cost per engine £6719)</p>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_92a.jpg"/></p>
<p class="fig-caption"><b>GWR Coat of Arms incorporating the shields of London (left) and Bristol (right)</b></p>
</div>
</a><p class="h1"><a id="page_67"></a><a id="page_68"><b>On the Works</b></a></p><a id="page_68">
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_93.jpg"/></p>
<p class="fig-caption"><b>No 5003 LULWORTH CASTLE after withdrawal at Swindon, in August 1962. <i>John Chalcroft/Rail Photoprints</i></b></p>
</div>
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_94.jpg"/></p>
<p class="fig-caption"><b>No 5011 TINTAGEL CASTLE at Swindon following a ‘Heavy Intermediate’ overhaul, June 1955. Note the parallel barrel buffers and original design curved inside cylinder casing. <i>Rail Photoprints Collection</i></b></p>
</div>
</a><div class="fig"><a id="page_68">
</a><p class="fig-img"><a id="page_68"></a><a id="page_69"><img alt="" class="image" src="images/ufig_95.jpg"/></a></p><a id="page_69">
<p class="fig-caption"><b>No 5014 GOODRICH CASTLE at Swindon following a ‘Heavy Intermediate’ overhaul in 1957. Note tapered barrel buffers and box pattern inside cylinder casing, sandboxes under cab floor. The ‘Castle’ is in the company ‘Hall’ class No 5951 CLYFFE HALL. <i>Mike Morant Collection</i></b></p>
</a></div><a id="page_69">
<div class="fig">
<p class="fig-img"><img alt="" class="image" src="images/ufig_96.jpg"/></p>
<p class="fig-caption"><b>No 5035 COITY CASTLE at Swindon following a ‘Heavy Intermediate’ overhaul in 1954. The ‘Castle’ is in the company of BR ‘Standard Britannia’ class No 70016 ARIEL. <i>Rail Photoprints Collection</i></b></p>
</div>
</a></div><a id="page_69">
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 08_Chapter02.vue?27b18268